import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./FortuneWheel.module.css";
import WheelComponent from "../../Components/WheelComponents/WheelComponent";
import ActionButtons from "../../Components/WheelComponents/ActionButtons";
import { getUserBalance, getSpinInfo } from "../../Services/Requests.ts";
import useApplicationSettings from "../../Store/ApplicationSettings.js";
import useUserStore from "../../Store/UserStore.js";
import { useNotificationStore } from "../../Store/NotificationStore.ts";

const PRIZES = [
  { amount: "1.0", type: "USDT" },
  { amount: "100", type: "DC" },
  { amount: "0.05", type: "USDT" },
  { amount: "50", type: "DC" },
  { amount: "0.5", type: "USDT" },
  { amount: "20", type: "DC" },
  { amount: "0.01", type: "USDT" },
  { amount: "150", type: "DC" },
];

const FortuneWheel = () => {
  const { tickets, setTickets } = useUserStore((state) => ({
    tickets: state.tickets,
    setTickets: state.setTickets,
  }));

  const [isSpinning, setIsSpinning] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPrize, setCurrentPrize] = useState(null);
  const wheelRef = useRef(null);
  const navigate = useNavigate();
  const setBalanceDC = useUserStore((state) => state.setBalanceDC);
  const setBalanceUSDT = useUserStore((state) => state.setBalanceUSDT);

  const { setShowMenubar } = useApplicationSettings((state) => ({
    setShowMenubar: state.setShowMenubar,
  }));
  const setNotification = useNotificationStore(
    (state) => state.setNotification
  );

  const tg = window.Telegram.WebApp;
  const [isWheelInitialized, setIsWheelInitialized] = useState(false);

  useEffect(() => {
    getUserBalance();
    tg.BackButton.show();
    tg.BackButton.onClick(() => navigate(-1));
    setShowMenubar(false);

    return () => {
      setShowMenubar(true);
    };
  }, [navigate, setShowMenubar, tg.BackButton]);

  const findSectorIndex = useCallback(
    (targetValue, targetCurrency) =>
      PRIZES.findIndex(
        (prize) =>
          prize.amount === String(targetValue) && prize.type === targetCurrency
      ),
    []
  );

  const handleSpinResult = useCallback((resultIndex) => {
    console.log("Вращение началось, целевой индекс:", resultIndex);
  }, []);

  const handleSpinComplete = useCallback(
    (resultIndex) => {
      setIsSpinning(false);
      const prize = PRIZES[resultIndex];
      console.log(prize);

      // Сохраняем приз в состоянии для отслеживания
      setCurrentPrize(prize);

      setNotification({
        type: "Success",
        message: `Поздравляем! Ты выиграл:`,
        rewards: [{ amount: prize.amount, type: prize.type }],
      });
    },
    [setNotification]
  );

  useEffect(() => {
    if (currentPrize) {
      const currentState = useUserStore.getState();
      const currentBalanceDC = currentState.balanceDC;
      const currentBalanceUSDT = currentState.balanceUSDT;

      if (currentPrize.type === "USDT") {
        setBalanceUSDT(currentBalanceUSDT + Number(currentPrize.amount));
      } else {
        setBalanceDC(currentBalanceDC + Number(currentPrize.amount));
      }

      setTimeout(() => {
        getUserBalance();
      }, 100);

      setCurrentPrize(null);
    }
  }, [currentPrize, setBalanceDC, setBalanceUSDT]);

  const handleSpinClick = useCallback(async () => {
    if (isSpinning || !wheelRef.current || isNaN(tickets) || tickets <= 0)
      return;

    if (!wheelRef.current.isInitialized()) {
      console.warn("Колесо еще не инициализировано");
      return;
    }

    try {
      setIsLoading(true);

      const response = await getSpinInfo();

      if (response?.reward) {
        const targetIndex = findSectorIndex(
          String(response.reward.amount),
          response.reward.type
        );

        if (targetIndex !== -1) {
          setTickets(response.tickets);
          wheelRef.current.spin(targetIndex);
          setIsSpinning(true);
        } else {
          throw new Error("Сектор не найден");
        }
      }
    } catch (error) {
      console.error("Ошибка при вращении:", error);
      setIsSpinning(false);
    } finally {
      setIsLoading(false);
    }
  }, [isSpinning, tickets, findSectorIndex, setTickets]);

  const handleNavigation = {
    vouchers: () => navigate("/vouchers"),
    tasks: () => navigate("/earn"),
  };

  const handleWheelInitialized = useCallback(() => {
    setIsWheelInitialized(true);
    setTimeout(() => {
      setIsButtonEnabled(true);
      setIsLoading(false);
    }, 600); // Задержка в 0.4 секунды
  }, []);

  return (
    <div className={styles.fortuneWheelContainer}>
      <div className={styles.header}>
        <h1 className={styles.title}>Колесо Фортуны</h1>
        <p className={styles.description}>
          Выполняй задания, собирай билеты
          <br />и испытай свою удачу в колесе фортуны!
        </p>
        <p className={styles.tickets}>
          Доступно билетов: <span>{tickets}</span>
        </p>
      </div>

      <WheelComponent
        onSpin={handleSpinResult}
        onSpinComplete={handleSpinComplete}
        onInitialized={handleWheelInitialized}
        ref={wheelRef}
      />

      <ActionButtons
        onVouchersClick={handleNavigation.vouchers}
        onTasksClick={handleNavigation.tasks}
        onSpinClick={handleSpinClick}
        tickets={tickets}
        disabled={
          isSpinning || !isWheelInitialized || !isButtonEnabled || isLoading
        }
        isLoading={isLoading}
        setNotification={setNotification}
      />
    </div>
  );
};

export default FortuneWheel;
